function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, useOpenUnsavedChangesModal } from 'seagull';
import { routes } from "../../constants";
import { useEventReporter, useOverlayContext, useIoContext, useStore, useStoreActions } from "../../contexts";
import { handleRequest } from "../../infra/utils";
import { proposalTemplateModel } from "../../models";
import { AgreementName } from "./AgreementName";
import { fromAgreementStudioCommon } from "./AgreementStudioCommon";
import { SubmitType } from "./AgreementStudioWithLegalTermsModal";
import AgreementStudioWithLegalTermsModal from "./AgreementStudioWithLegalTermsModal";
import { ClientPreview } from "../ClientPreview/ClientPreview";
import { ContactModel } from "../../models/ContactModel";
import { Intent } from "../../infra/access-control";
const saveTemplateLoader = 'saveTemplate';
export function AgreementStudioTemplateMode(props) {
  const {
    template
  } = props;
  const history = useHistory();
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const {
    reportButtonClick
  } = useEventReporter();
  const isEditingTemplate = template != null;
  const scrollToTopRef = useRef(null);
  const [templateName, setTemplateName] = useState(template === null || template === void 0 ? void 0 : template.title);
  const [templateNameInvalid, setTemplateNameInvalid] = useState(false);
  const [showTemplateNameInvalid, setShowTemplateNameInvalid] = useState(false);
  const {
    getNow
  } = useIoContext();
  const contacts = useStore(state => state.contacts);
  const [contactsMap, sampleContact] = useMemo(() => {
    var _contacts$data;
    const contactsData = (_contacts$data = contacts.data) !== null && _contacts$data !== void 0 ? _contacts$data : [];
    const contactsMap = {};
    let sampleContact = ContactModel.makeSample({
      createdAt: getNow(),
      vendorId: props.worksAt.id
    });
    for (let i = 0; i < contactsData.length; i++) {
      const contact = contactsData[i];
      if (contact.isSample()) {
        sampleContact = contact;
      } else {
        contactsMap[contact.id()] = contact;
      }
    }
    return [contactsMap, sampleContact];
  }, [contacts, props.worksAt.id]);
  function goToProposalTemplates() {
    history.push(routes.proposalTemplates);
  }
  const onSubmit = async data => {
    logBI(isEditingTemplate ? 10493 : 10492);
    if (templateName == null || templateName === '' || templateNameInvalid) {
      setShowTemplateNameInvalid(true);
      if (scrollToTopRef.current != null) scrollToTopRef.current();
      return;
    }
    const templateToSave = fromAgreementStudioCommon(data, templateName);
    overlayContext.startLoader(saveTemplateLoader);
    const result = await handleRequest(isEditingTemplate ? api.proposalTemplates.save(template.id, templateToSave) : api.proposalTemplates.create(templateToSave).then(() => {}));
    if (!result.success) {
      const {
        text
      } = result.error.createRelationshipErrorText();
      overlayContext.showErrorToast(text);
      overlayContext.stopLoader(saveTemplateLoader);
      return;
    }
    goToProposalTemplates();
    overlayContext.showSuccessToast('Proposal template successfully saved', 'proposal-template-saved-toast');
    overlayContext.stopLoader(saveTemplateLoader);
  };
  const templateToCommonWithId = template => ({
    id: template.id,
    coverLetter: template.coverLetter,
    extraClause: template.extraClause,
    requirePaymentMethod: template.requirePaymentMethod,
    allowManualPayments: template.allowManualPayments,
    isExistingCustomer: template.isExistingCustomer,
    customNotificationText: template.customNotificationText,
    services: template.services,
    serviceBundles: template.serviceBundles,
    netTerms: template.netTerms,
    legalTerms: template.legalTerms,
    coverCreditCardFees: template.feeControls.coverCreditCardFees,
    /** Will automatic ongoing services be billed at the start or end of the billing-period. */
    amendmentsApprovalMethod: template.amendmentsApprovalMethod,
    invoicesApprovalMethod: template.invoicesApprovalMethod,
    requestAccountantAccess: template.requestAccountantAccess,
    introVideo: template.introVideo,
    expirationDate: template.expirationDate
  });
  const openPreviewModal = data => {
    const draft = proposalTemplateModel.toPreviewData(proposalTemplateModel.fromDTO(fromAgreementStudioCommon(data, templateName || '')));
    reportButtonClick('agreement-previewed-agreement');
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: modalProps => /*#__PURE__*/React.createElement(ClientPreview, _extends({
        type: "draft",
        draftInfo: {
          draft,
          sampleContact,
          contactsMap,
          legalTerms: props.legalTermsDocuments
        }
      }, modalProps))
    });
  };
  const ui = isEditingTemplate ? {
    modalProps: {
      draft: templateToCommonWithId(template),
      modalTitle: 'Edit proposal template'
    },
    buttonProps: {
      children: 'Save changes'
    }
  } : {
    modalProps: {
      draft: null,
      modalTitle: 'Create proposal template'
    },
    buttonProps: {
      children: 'Save template',
      intent: Intent.CreateProposalTemplate
    }
  };
  return /*#__PURE__*/React.createElement(AgreementStudioWithLegalTermsModal, _extends({}, props, ui.modalProps, {
    effectiveDate: null,
    agreementTitleMP: /*#__PURE__*/React.createElement(AgreementName, {
      agreementTitle: templateName || '',
      setAgreementTitle: setTemplateName,
      setAgreementTitleValid: value => setTemplateNameInvalid(!value),
      showWarning: showTemplateNameInvalid,
      label: "Template name",
      testid: "agreement-studio-customer-details-input-display-title"
    }),
    doScrollToTopRef: scrollToTopRef,
    onSubmitCb: onSubmit,
    shouldClose: hasUnsavedChanges => hasUnsavedChanges ? openUnsavedChangesModal({
      testid: 'warn-unsaved-changes-modal'
    }) : Promise.resolve(true),
    onClose: goToProposalTemplates,
    additionalAutoSaveDependencies: [templateName],
    openPreviewModal: openPreviewModal,
    renderSubmitButton: _ref => {
      let {
        testid,
        className,
        onSubmit
      } = _ref;
      return /*#__PURE__*/React.createElement(Button, _extends({
        "data-testid": testid,
        className: className,
        onClick: () => onSubmit(SubmitType.SendProposal)
      }, ui.buttonProps));
    }
  }));
}